import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { resetPassword, clearError } from "../store/authSlice";
import { useNavigate, useSearchParams } from "react-router-dom";

import "@fortawesome/fontawesome-free/css/all.min.css";

interface ResetPasswordFormProps {
  onBackToLogin?: () => void;
}

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({
  onBackToLogin,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isLoading, error } = useAppSelector((state) => state.auth);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const token = searchParams.get("token");
  const isButtonDisabled =
    !newPassword ||
    !confirmPassword ||
    newPassword !== confirmPassword ||
    !token;

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      console.error("No token found in URL");
      return;
    }

    if (newPassword !== confirmPassword) {
      return;
    }

    try {
      await dispatch(resetPassword({ token, newPassword })).unwrap();
      setIsSuccess(true);
      setTimeout(() => {
        navigate("/sign_in");
      }, 3000);
    } catch (error) {
      console.error("Reset password failed:", error);
    }
  };

  const handleBackToLogin = () => {
    if (onBackToLogin) {
      onBackToLogin();
    }
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <div className="flex justify-center items-center login-form-width">
      <div className="w-[444px] p-6 rounded-2xl border border-lighterGray login-box-shadow h-fit">
        <h2 className="text-h1 font-bold text-darkestSlate pb-2">
          Forgot Password?
        </h2>
        <button
          type="button"
          className="text-h3 font-semibold text-darkBlue hover:underline mb-6 text-start"
          onClick={handleBackToLogin}
        >
          Back to Log In
        </button>

        {!token && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            <p className="text-sm">
              Invalid or missing reset token. Please use the link from your
              email.
            </p>
          </div>
        )}
        {!isSuccess ? (
          <form className="flex flex-col" onSubmit={handleSubmit}>
            <label
              htmlFor="new-password"
              className="text-h4 font-medium text-darkGray pb-1"
            >
              New Password *
            </label>
            <div className="relative mb-1">
              <input
                type={showNewPassword ? "text" : "password"}
                name="newPassword"
                placeholder="New Password"
                id="new-password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  if (error) {
                    dispatch(clearError());
                  }
                }}
                className={`h-10 px-3 py-2.5 pr-10 rounded-xl bg-lightestGray border w-full ${
                  error ? "border-red-500" : "border-lighterGray"
                }`}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-darkGray hover:text-darkestSlate"
                onClick={toggleNewPasswordVisibility}
              >
                <i
                  className={`fas ${
                    !showNewPassword ? "fa-eye-slash" : "fa-eye"
                  }`}
                ></i>
              </button>
            </div>

            <label
              htmlFor="confirm-password"
              className="text-h4 font-medium text-darkGray pb-1 mt-3"
            >
              Confirm New Password *
            </label>
            <div className="relative mb-1">
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                placeholder="Confirm New Password"
                id="confirm-password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (error) {
                    dispatch(clearError());
                  }
                }}
                className={`h-10 px-3 py-2.5 pr-10 rounded-xl bg-lightestGray border w-full ${
                  error ? "border-red-500" : "border-lighterGray"
                }`}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-darkGray hover:text-darkestSlate"
                onClick={toggleConfirmPasswordVisibility}
              >
                <i
                  className={`fas ${
                    !showConfirmPassword ? "fa-eye-slash" : "fa-eye"
                  }`}
                ></i>
              </button>
            </div>

            {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

            {newPassword &&
              confirmPassword &&
              newPassword !== confirmPassword && (
                <p className="text-red-500 text-sm mb-4">
                  Passwords do not match
                </p>
              )}

            <button
              type="submit"
              className={`text-h3 font-semibold text-lightestGray h-10 rounded-3xl flex items-center justify-center gap-2 hover:bg-mediumGray mt-6 ${
                isButtonDisabled || isLoading
                  ? "bg-gray cursor-not-allowed"
                  : "bg-darkBlue"
              }`}
              disabled={isButtonDisabled || isLoading}
            >
              {isLoading ? "Updating..." : "Update Password"}
            </button>
          </form>
        ) : (
          <div className="flex flex-col">
            <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
              <p className="text-sm">
                Password updated successfully! Redirecting to login page...
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/sign_in")}
              className="text-h3 font-semibold text-darkBlue hover:underline"
            >
              Go to Login Now
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordForm;
